import { Gauge } from 'lucide-react'
import type { UsageWindow } from '@one-deployment/api'
import { useClaudeAccounts } from '@/features/claude/accounts'

/** Claude's own limit lines: "You've hit your 5-hour limit · resets 3:20am (UTC)", "Usage limit reached …". */
const LIMIT = /hit your (?:[\w-]+ )?limit|usage limit reached|limit reached\s*[·∙|]/i
export const isLimitText = (text: string) => LIMIT.test(text) && text.length < 300

/** "in 2h 10m (03:20)" within a day, else "Thu 09:00". */
export function whenReset(at: number, now = Date.now()) {
  const ms = at - now
  const clock = new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  if (ms <= 0) return 'now'
  if (ms < 86_400_000) {
    const h = Math.floor(ms / 3_600_000), m = Math.floor((ms % 3_600_000) / 60_000)
    return `in ${h ? `${h}h ` : ''}${m}m (${clock})`
  }
  return new Date(at).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' })
}

/**
 * A usage limit, said plainly in the chat instead of Claude's raw line: which
 * window is full and when each resets, read from the limited account's own
 * usage when there is one.
 */
export function LimitNotice({ text }: { text: string }) {
  const accounts = useClaudeAccounts().data?.accounts ?? []
  const limited = accounts.filter((a) => a.state === 'limited')
  const week = /week/i.test(text)
  const row = (name: string, w: UsageWindow | undefined) => w && (
    <div className="flex items-center justify-between gap-4 text-xs">
      <span className={w.percent !== null && w.percent >= 100 ? 'text-err' : 'text-fg-subtle'}>{name} · {w.percent === null ? '—' : `${Math.round(w.percent)}%`}</span>
      {w.resetsAt ? <span className="text-fg-faint">resets {whenReset(w.resetsAt)}</span> : null}
    </div>
  )
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-err/30 bg-err/5 p-3">
      <div className="flex items-center gap-2 text-sm font-medium text-err"><Gauge size={14} />{week ? 'Weekly' : '5-hour'} Claude limit reached</div>
      {limited.length ? limited.map((a) => (
        <div key={a.id} className="space-y-1">
          {limited.length > 1 && <div className="text-xs text-fg">{a.label}</div>}
          {row('5 hours', a.usage.fiveHour)}
          {row('Week', a.usage.week)}
        </div>
      )) : <div className="text-xs text-fg-subtle">{text.replace(/^.*?limit\s*[·∙]?\s*/i, '').replace(/^resets/i, 'Resets') || text}</div>}
      <div className="text-xs text-fg-faint">Another account takes over when one is free; otherwise Claude carries on at the reset.</div>
    </div>
  )
}
